import { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { Search, Home as HomeIcon, Users, TrendingUp, Phone, Mail, MapPin, ArrowRight, Sparkles, Shield, Clock, Play, Scale, Bed, Bath, Square } from 'lucide-react';
import { Property } from '../types/property';
import { propertyAPI } from '../lib/property-api';
import taglineLogo from 'figma:asset/508c194fff736806cace6bacd6c3f0c79e18d044.png';
import phelloLogo from 'figma:asset/bfed315bca15e3f2e2036c6353cc43d546a68999.png';
import Slider from 'react-slick';
import { getContactEmail } from '@/config/runtime-config';

interface ModernHomeProps {
  onViewCatalog: () => void;
  onPropertySelect: (property: Property) => void;
}

// v2.1 spec: R X,XXX,XXX format
function formatPrice(price: number): string {
  if (!price && price !== 0) return 'Price on request';
  return `R ${price.toLocaleString('en-ZA')}`;
}

export function ModernHome({ onViewCatalog, onPropertySelect }: ModernHomeProps) {
  const [featuredProperties, setFeaturedProperties] = useState<Property[]>([]);
  const [propertyCount, setPropertyCount] = useState(0);

  // Load real properties from Supabase KV — single source of truth
  useEffect(() => {
    async function loadFeatured() {
      try {
        const { listings: all } = await propertyAPI.getProperties();
        const published = all.filter(p => p.status === 'Published');
        setPropertyCount(published.length);

        // Pick up to 6 featured/newest properties for the homepage
        const featured = published
          .filter(p => p.flags?.featured)
          .slice(0, 6);

        if (featured.length < 3) {
          // Not enough featured — fill with newest published listings
          const newest = published
            .filter(p => !featured.some(f => f.id === p.id))
            .sort((a, b) => new Date(b.listingDate).getTime() - new Date(a.listingDate).getTime())
            .slice(0, 6 - featured.length);
          featured.push(...newest);
        }
        setFeaturedProperties(featured.slice(0, 6));
      } catch (err) {
        console.warn('Failed to load featured properties:', err);
      }
    }
    loadFeatured();
  }, []);

  const stats = [
    { icon: HomeIcon, label: 'Properties Listed', value: propertyCount > 0 ? `${propertyCount}+` : '140+' },
    { icon: Users, label: 'Happy Clients', value: '850+' },
    { icon: TrendingUp, label: 'Successful Sales', value: '750+' }
  ];

  const features = [
    {
      title: 'Lightning Fast',
      description: 'Find your dream property in seconds with our powerful search engine.',
      icon: Sparkles
    },
    {
      title: 'Secure & Trusted',
      description: 'Your data is protected with enterprise-grade security.',
      icon: Shield
    },
    {
      title: '24/7 Support',
      description: 'Our expert team is always here to help you.',
      icon: Clock
    }
  ];

  // Partners data - duplicated for carousel effect
  const partners = [
    {
      name: 'Phello Attorneys',
      logo: phelloLogo,
      services: ['Conveyancing', 'Property Law']
    },
    {
      name: 'Phello Attorneys',
      logo: phelloLogo,
      services: ['Conveyancing', 'Property Law']
    },
    {
      name: 'Phello Attorneys',
      logo: phelloLogo,
      services: ['Conveyancing', 'Property Law']
    },
    {
      name: 'Phello Attorneys',
      logo: phelloLogo,
      services: ['Conveyancing', 'Property Law']
    },
    {
      name: 'Phello Attorneys',
      logo: phelloLogo,
      services: ['Conveyancing', 'Property Law']
    }
  ];

  const partnerSliderSettings = {
    dots: false,
    infinite: true,
    speed: 3500,
    slidesToShow: 3,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 0,
    cssEase: 'linear',
    pauseOnHover: true,
    rtl: true,
    arrows: false,
    variableWidth: false,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
        }
      },
      {
        breakpoint: 640,
        settings: {
          slidesToShow: 1,
        }
      }
    ]
  };

  return (
    <div className="min-h-screen overflow-hidden pb-20 md:pb-0">
      {/* Hero Section with Gradient */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
        {/* Animated Background Gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-background to-accent/20">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(88,101,242,0.1),transparent_50%)]" />
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-accent/10 rounded-full blur-3xl animate-pulse delay-1000" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-5xl mx-auto text-center"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-full mb-8"
            >
              <Sparkles className="w-4 h-4 text-primary" />
              <span className="text-sm text-foreground">South Africa's Premier Property Platform</span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-br from-foreground via-foreground to-muted-foreground bg-clip-text text-transparent leading-tight"
            >
              Find Your Dream Home<br />
              <span className="text-primary">Lightning Fast</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-xl md:text-2xl text-muted-foreground mb-10 max-w-3xl mx-auto leading-relaxed"
            >
              Discover exceptional properties across South Africa with our cutting-edge platform.
              Your perfect home is just a search away.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="flex flex-col sm:flex-row gap-4 justify-center items-center"
            >
              <Button
                size="lg"
                onClick={onViewCatalog}
                className="px-8 py-6 text-lg bg-primary hover:bg-primary/90 group transition-all duration-300 shadow-lg shadow-primary/25"
                data-testid="modern-home-browse-properties-button"
              >
                <Search className="w-5 h-5 mr-2 group-hover:rotate-12 transition-transform" />
                Browse Properties
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </motion.div>

            {/* Floating Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="grid grid-cols-3 gap-8 mt-20 max-w-3xl mx-auto"
            >
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 1 + index * 0.1 }}
                  className="text-center"
                >
                  <div className="text-3xl md:text-4xl font-bold text-primary mb-2">{stat.value}</div>
                  <div className="text-sm text-muted-foreground">{stat.label}</div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* CEO Video Section - A Word From The CEO */}
      <section className="py-24 relative">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5" />
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-5xl mx-auto"
          >
            <div className="text-center mb-10">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-full mb-6"
              >
                <Play className="w-4 h-4 text-primary" />
                <span className="text-sm text-foreground">A Word From The CEO</span>
              </motion.div>
              <h2 className="text-4xl md:text-5xl font-bold mb-4">Discover Something Property</h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Watch our CEO, Teboho Phello, share the story behind Something Property and our commitment 
                to making your property dreams come true.
              </p>
            </div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <Card className="overflow-hidden border-border/50 bg-card/50 backdrop-blur-sm">
                <div className="aspect-video bg-black relative">
                  <iframe
                    className="w-full h-full"
                    src="https://www.youtube.com/embed/kheymRSiiwo?start=12"
                    title="Something Property - A Word From The CEO"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    style={{ border: 0 }}
                  />
                </div>
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center flex-shrink-0">
                      <Users className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2 text-lg">Meet Our Leadership</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        <strong className="text-foreground">Teboho Phello</strong> - CEO & Founder. An entrepreneur and property enthusiast, 
                        admitted Attorney since 2016, and specialist Property Lawyer (Conveyancing Attorney and Notary).
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 relative">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Why Choose Us</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Experience the future of property search with our innovative platform
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="border-border/50 bg-card/50 backdrop-blur-sm hover:border-primary/50 transition-all duration-300 group h-full">
                  <CardHeader>
                    <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                      <feature.icon className="w-6 h-6 text-primary" />
                    </div>
                    <CardTitle className="text-xl">{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-base">{feature.description}</CardDescription>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Properties — now from Supabase KV */}
      <section className="py-24 relative">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Featured Properties</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Handpicked premium properties across South Africa's most desirable locations
            </p>
          </motion.div>

          {featuredProperties.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
              {featuredProperties.map((property, index) => {
                const coverImage = property.media?.find(m => m.isCover) || property.media?.[0];
                const locationDisplay = [property.address?.suburb, property.address?.city].filter(Boolean).join(', ');
                return (
                  <motion.div
                    key={property.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <Card
                      className="overflow-hidden border-border/50 bg-card/50 backdrop-blur-sm hover:border-primary/50 transition-all duration-300 cursor-pointer group h-full"
                      onClick={() => onPropertySelect(property)}
                    >
                      <div className="aspect-[4/3] bg-muted relative overflow-hidden">
                        {coverImage ? (
                          <ImageWithFallback
                            src={coverImage.url}
                            alt={coverImage.alt || property.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        ) : (
                          <div className="w-full h-full bg-muted flex items-center justify-center">
                            <Square className="w-12 h-12 text-muted-foreground/30" />
                          </div>
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                        {property.flags?.featured && (
                          <Badge className="absolute top-4 right-4 bg-primary/90 backdrop-blur-sm border-0">
                            Featured
                          </Badge>
                        )}
                        {property.flags?.new && !property.flags?.featured && (
                          <Badge className="absolute top-4 right-4 bg-blue-600/90 backdrop-blur-sm border-0 text-white">
                            New
                          </Badge>
                        )}
                      </div>
                      <CardHeader className="pb-3">
                        <CardTitle className="line-clamp-1 group-hover:text-primary transition-colors text-base">
                          {property.title}
                        </CardTitle>
                        {locationDisplay && (
                          <CardDescription className="flex items-center text-sm">
                            <MapPin className="w-3.5 h-3.5 mr-1 flex-shrink-0" />
                            {locationDisplay}
                          </CardDescription>
                        )}
                      </CardHeader>
                      <CardContent>
                        <div className="flex justify-between items-center mb-3">
                          <span className="text-2xl font-bold text-primary">
                            {formatPrice(property.price)}
                          </span>
                        </div>
                        <div className="flex gap-4 text-sm text-muted-foreground">
                          {property.facts?.bedrooms != null && property.facts.bedrooms > 0 && (
                            <span className="flex items-center gap-1">
                              <Bed className="w-3.5 h-3.5 text-primary/70" />
                              {property.facts.bedrooms} Bed
                            </span>
                          )}
                          {property.facts?.bathrooms != null && property.facts.bathrooms > 0 && (
                            <span className="flex items-center gap-1">
                              <Bath className="w-3.5 h-3.5 text-primary/70" />
                              {property.facts.bathrooms} Bath
                            </span>
                          )}
                          {property.facts?.floorSize != null && property.facts.floorSize > 0 && (
                            <span className="flex items-center gap-1">
                              <Square className="w-3.5 h-3.5 text-primary/70" />
                              {property.facts.floorSize}m²
                            </span>
                          )}
                          {property.facts?.erfSize != null && property.facts.erfSize > 0 && !property.facts?.floorSize && (
                            <span className="flex items-center gap-1">
                              <Square className="w-3.5 h-3.5 text-primary/70" />
                              {property.facts.erfSize}m²
                            </span>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground mb-12">
              <p>Properties are being synced. Check back shortly.</p>
            </div>
          )}

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            <Button
              size="lg"
              variant="outline"
              onClick={onViewCatalog}
              className="px-8 py-6 text-lg border-border/50 hover:border-primary/50 hover:bg-primary/5 group"
              data-testid="modern-home-view-all-properties-button"
            >
              View All Properties
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Partners Section - Carousel */}
      <section className="py-20 relative overflow-hidden bg-muted/30">
        <div className="absolute inset-0 bg-gradient-to-br from-accent/10 via-transparent to-primary/10" />
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-full mb-6"
            >
              <Scale className="w-4 h-4 text-primary" />
              <span className="text-sm text-foreground">Trusted Legal Partnership</span>
            </motion.div>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Our Trusted Partners</h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="partners-carousel"
          >
            <Slider {...partnerSliderSettings}>
              {partners.map((partner, index) => (
                <div key={index} className="px-3">
                  <Card className="border-border/50 bg-card backdrop-blur-sm hover:border-primary/50 transition-all duration-300 shadow-lg mx-auto">
                    <CardContent className="p-8 text-center">
                      <div className="flex justify-center mb-6">
                        <img
                          src={partner.logo}
                          alt={partner.name}
                          className="max-w-[248px] max-h-24 object-contain"
                        />
                      </div>
                      <div className="flex flex-wrap gap-2 justify-center">
                        {partner.services.map((service, idx) => (
                          <Badge 
                            key={idx} 
                            variant="secondary" 
                            className="bg-primary/10 text-primary border-primary/20 px-3 py-1"
                          >
                            {service}
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              ))}
            </Slider>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-background to-accent/10" />
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto text-center"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Ready to Find Your Perfect Home?
            </h2>
            <p className="text-xl text-muted-foreground mb-10">
              Join thousands of happy clients who found their dream properties with us
            </p>
            <Button
              size="lg"
              onClick={onViewCatalog}
              className="px-10 py-7 text-lg bg-primary hover:bg-primary/90 group shadow-xl shadow-primary/25"
              data-testid="modern-home-get-started-button"
            >
              Get Started Now
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24 relative">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-4">Get In Touch</h2>
              <p className="text-xl text-muted-foreground">
                Our expert team is ready to help you every step of the way
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { icon: Phone, title: 'Call Us', value: '011 862 1790', subtitle: '012 824 0790' },
                { icon: Mail, title: 'Email Us', value: getContactEmail(), subtitle: "We'll respond within 24 hours" },
                { icon: MapPin, title: 'Visit Us', value: '18 Drakensberg Road', subtitle: 'Germiston, 1424' }
              ].map((contact, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card className="border-border/50 bg-card/50 backdrop-blur-sm hover:border-primary/50 transition-all duration-300 text-center group h-full">
                    <CardHeader>
                      <div className="flex justify-center mb-4">
                        <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                          <contact.icon className="w-7 h-7 text-primary" />
                        </div>
                      </div>
                      <CardTitle className="text-xl">{contact.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-foreground mb-2">{contact.value}</p>
                      <p className="text-sm text-muted-foreground">{contact.subtitle}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
